import { HorizontalScroll } from "./HorizontalScroll";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const STATUS_STYLES = {
  scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  en_route: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  on_site: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  completed: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
  cancelled: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  void: "bg-neutral-200 text-neutral-700 border-neutral-400",
  open: "bg-red-500/10 text-red-400 border-red-500/30",
  in_progress: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  invoiced: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  draft: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  confirmed: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  submitted: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  fulfilled: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
  delivered: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  on_rent: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  returned: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  in_shop: "bg-red-500/10 text-red-400 border-red-500/30",
  rented_out: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  retired: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  suspended: "bg-red-500/10 text-red-400 border-red-500/30",
  trial: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  starter: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  pro: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  enterprise: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
  sent: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
};

export const StatusBadge = ({ status, testId }) => (
  <span
    data-testid={testId || `status-${status}`}
    className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-mono uppercase tracking-wider ${STATUS_STYLES[status] || "bg-slate-500/10 text-slate-300 border-slate-500/30"}`}
  >
    {String(status || "—").replace(/_/g, " ")}
  </span>
);

export const PageHeader = ({ title, subtitle, children, testId }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6" data-testid={testId}>
    <div>
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-500/80 mb-1">WellFleetLogic</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-50">{title}</h1>
      {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-3 max-w-full">{children}</div>}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, accent = "text-amber-400", testId }) => (
  <div
    data-testid={testId}
    className="bg-card border border-slate-700/60 rounded-lg p-5 hover:border-amber-500/40 transition-colors duration-200"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400">{label}</span>
      {Icon && <Icon size={18} className={accent} />}
    </div>
    <div className="font-heading text-3xl font-bold text-slate-50">{value}</div>
  </div>
);

export const EmptyState = ({ message, testId }) => (
  <div
    data-testid={testId}
    className="border border-dashed border-slate-700 rounded-lg p-10 text-center text-sm text-slate-500 font-mono uppercase tracking-widest"
  >
    {message}
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-400 mb-1.5">{label}</span>
    {children}
  </label>
);

export const TextInput = ({ testId, ...props }) => (
  <input
    data-testid={testId}
    className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors duration-200"
    {...props}
  />
);

export const TextArea = ({ testId, className = "", rows = 3, ...props }) => (
  <textarea
    data-testid={testId}
    rows={rows}
    className={`w-full bg-slate-900/80 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors duration-200 resize-y ${className}`}
    {...props}
  />
);

export const SelectInput = ({ testId, options, placeholder, className = "", ...props }) => (
  <select
    data-testid={testId}
    className={`w-full bg-slate-900/80 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors duration-200 ${className}`}
    {...props}
  >
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={o.value} value={o.value} disabled={o.disabled}>
        {o.label}
      </option>
    ))}
  </select>
);

export const ActionButton = ({ testId, variant = "primary", className = "", ...props }) => {
  const styles = {
    primary:
      "bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold shadow-glow",
    secondary:
      "bg-slate-800 text-slate-200 border border-slate-700 hover:border-amber-500/50 hover:text-amber-300",
    danger: "bg-red-500/10 text-red-400 border border-red-500/40 hover:bg-red-500/20",
  };
  return (
    <button
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
      {...props}
    />
  );
};

export const FormDialog = ({ open, onOpenChange, title, children, testId, className = "max-w-lg" }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid={testId}
      className={`bg-[#FFFFFF] border border-slate-700 text-slate-100 ${className} max-h-[85vh] overflow-y-auto`}
    >
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-slate-50">{title}</DialogTitle>
        <DialogDescription className="sr-only">{title}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">{children}</div>
    </DialogContent>
  </Dialog>
);

export const DataTable = ({ columns, rows, rowKey, testId, renderRow }) => (
  <HorizontalScroll className="bg-card border border-slate-700/60 rounded-lg overflow-hidden" testId={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700/60 bg-slate-900/60">
            {columns.map((c) => (
              <th
                key={c}
                className="text-left px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] text-slate-400 whitespace-nowrap"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows.map((r) => renderRow(r))}</tbody>
      </table>
  </HorizontalScroll>
);

export const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 border-b border-slate-800/60 text-slate-300 whitespace-nowrap ${className}`}>{children}</td>
);

export const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center" data-testid="loading-screen">
    <div className="flex items-center gap-3 text-amber-500 font-mono text-sm uppercase tracking-[0.2em]">
      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse-dot" />
      Loading WellFleetLogic
    </div>
  </div>
);


